import { LockIcon } from "./icons";
import { Notice } from "./Notice";

/** The two channel-level denial reasons this notice explains. */
export type ChannelBlockReason = "channel_disabled" | "persona_no_channel";

interface ChannelUnavailableNoticeProps {
  /** Human-readable host name ("Word", "PowerPoint", "Outlook"), or null when unknown. */
  readonly hostLabel: string | null;
  /**
   * Why the channel is unavailable. "channel_disabled" = this Office app is
   * switched off for the whole workspace on the Studio Channels page;
   * "persona_no_channel" = the workspace has the channel on but the user's persona
   * has no access to it. Defaults to "persona_no_channel" (older payloads sent no
   * reason and the persona gate was the only channel gate).
   */
  readonly reason?: ChannelBlockReason;
}

/**
 * Full-pane "this add-in is not available here" state. Shown when the backend
 * reports the user has no access to the current channel (HasChannelAccess ===
 * false). The message is exact per `reason` and names the Office app, so the user
 * knows whether an admin turned the channel off for the workspace or their
 * persona simply lacks access. It replaces the entire functional surface; only an
 * admin change in AiVA Studio restores it (no Retry — reopen the task pane once
 * restored).
 */
export function ChannelUnavailableNotice({
  hostLabel,
  reason = "persona_no_channel",
}: Readonly<ChannelUnavailableNoticeProps>) {
  const app = hostLabel ?? "this app";
  const message =
    reason === "channel_disabled"
      ? `The ${app} channel has been disabled for this workspace in AiVA Studio. Contact an administrator to enable it.`
      : `Your persona does not have access to AiVA in ${app}. Contact an administrator to request access to this channel in AiVA Studio.`;
  return <Notice icon={<LockIcon />} title="AiVA is not available here" message={message} />;
}
